import React, { useEffect, useRef } from "react";
import { Check, X } from "lucide-react";
import TrayRowActions from "./tray-row-actions";
import TrayHolderInfo from "./tray-holder-info";
import { isTrayHeld } from "./tray-holder";

export const TRAY_ROW_HEIGHT = 50;
const TRAY_GRID_COLUMNS = "6rem minmax(14rem, 1fr) 12rem 8rem";
// Active trays get a leading checkbox column for bulk label printing.
export const trayGridColumns = (selectable) =>
  selectable ? `3rem ${TRAY_GRID_COLUMNS}` : TRAY_GRID_COLUMNS;
// tray_master.tray_name is varchar(255)
const TRAY_NAME_MAX_LENGTH = 255;

export const trayCellClass =
  "px-[8px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap overflow-hidden text-ellipsis";

const inputClass =
  "h-[34px] w-full rounded-[6px] px-2 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] focus:outline-none focus:border-[var(--input-focus)]";

/**
 * Inline tray name field: Enter saves, Escape cancels. Used by the edit and add
 * rows. Read-only (not disabled) while saving, so it keeps focus and the user
 * can correct the name straight after a failed save.
 */
export const TrayNameInput = ({ value, onChange, onSave, onCancel, saving }) => (
  <input
    autoFocus
    type="text"
    value={value}
    maxLength={TRAY_NAME_MAX_LENGTH}
    placeholder="Tray name"
    readOnly={saving}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (!saving) onSave();
      }
      if (event.key === "Escape") onCancel();
    }}
    className={inputClass}
  />
);

/**
 * Selection checkbox, same look as the transaction tables'. `indeterminate`
 * (the header's "some selected" dash) can only be set from script.
 */
export const SelectCheckbox = ({ checked, indeterminate = false, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      title={label}
      checked={checked}
      onChange={onChange}
      className="custom-checkbox form-check-input cursor-pointer"
    />
  );
};

export const SaveCancelButtons = ({ onSave, onCancel, saving }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      title="Save"
      disabled={saving}
      onClick={onSave}
      className="flex items-center hover:opacity-70 disabled:opacity-40"
    >
      <Check size={18} color="#16A34A" />
    </button>
    <button
      type="button"
      title="Cancel"
      disabled={saving}
      onClick={onCancel}
      className="flex items-center hover:opacity-70 disabled:opacity-40"
    >
      <X size={18} color="#98A2B3" />
    </button>
  </div>
);

/**
 * One react-window row. The extra row past the last tray (while more pages
 * remain) shows the loading indicator, or a Retry link if that page failed.
 */
const TrayListRow = ({
  ariaAttributes,
  index,
  style,
  trays,
  editingId,
  draftName,
  setDraftName,
  saving,
  onSaveEdit,
  onCancelEdit,
  menuItemsFor,
  loadError,
  onRetry,
  selectable,
  selected,
  onToggleSelect,
}) => {
  const tray = trays[index];

  if (!tray) {
    return (
      <div
        style={style}
        className="flex items-center justify-center gap-2 text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
      >
        {loadError ? (
          <>
            Couldn't load more trays.
            <button type="button" className="text-primary underline" onClick={onRetry}>
              Retry
            </button>
          </>
        ) : (
          "Loading more trays..."
        )}
      </div>
    );
  }

  const isEditing = tray.tray_id === editingId;

  return (
    <div
      {...ariaAttributes}
      style={{ ...style, gridTemplateColumns: trayGridColumns(selectable) }}
      className="grid items-center border-b border-[var(--list-table-border-color)] hover:bg-[var(--list-table-hover-bg)]"
    >
      {selectable && (
        <div className={trayCellClass}>
          <SelectCheckbox
            label={`Select ${tray.tray_name || "tray"}`}
            checked={tray.tray_id in selected}
            onChange={() => onToggleSelect(tray)}
          />
        </div>
      )}
      <div className={trayCellClass}>{index + 1}</div>
      <div className={trayCellClass}>
        {isEditing ? (
          <TrayNameInput
            value={draftName}
            onChange={setDraftName}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
            saving={saving}
          />
        ) : (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="overflow-hidden text-ellipsis">{tray.tray_name || "--"}</span>
            {isTrayHeld(tray) && <TrayHolderInfo tray={tray} />}
          </span>
        )}
      </div>
      <div className={trayCellClass}>{tray.barcode ?? "--"}</div>
      <div className={trayCellClass}>
        {isEditing ? (
          <SaveCancelButtons onSave={onSaveEdit} onCancel={onCancelEdit} saving={saving} />
        ) : (
          <TrayRowActions items={menuItemsFor(tray)} />
        )}
      </div>
    </div>
  );
};

export default TrayListRow;
